import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { connectFirestoreEmulator, enableIndexedDbPersistence } from 'firebase/firestore';
import { connectAuthEmulator } from 'firebase/auth';
import { logError, logInfo } from '../utils/logger';

// Firebase configuration
// Validate required environment variables
const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID'
];

logInfo('Initializing Firebase configuration...');

const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);
if (missingVars.length > 0) {
  logError(`Missing required environment variables: ${missingVars.join(', ')}`);
  throw new Error(`Missing required environment variables: ${missingVars.join(', ')}`);
}

logInfo('Environment variables validated successfully');

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

logInfo('Firebase configuration loaded');

// Initialize Firebase services
let app;
let auth;
let db;
let storage;

try {
  logInfo('Starting Firebase initialization...');
  
  // Check if Firebase is already initialized
  app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  
  // Initialize Auth first
  auth = getAuth(app);
  
  // Initialize other services
  db = getFirestore(app);
  storage = getStorage(app);
  
  // Enable persistence for better offline support
  try {
    await enableIndexedDbPersistence(db, {
      synchronizeTabs: true,
      experimentalForceOwningTab: true
    });
    logInfo('Firestore persistence enabled successfully');
  } catch (err) {
    if (err.code === 'failed-precondition') {
      // Multiple tabs open, persistence can only be enabled in one tab.
      logInfo('Persistence already enabled in another tab');
    } else if (err.code === 'unimplemented') {
      logError('Browser does not support persistence');
    }
  }

  logInfo('Firebase services initialized successfully');
} catch (error) {
  logError('Failed to initialize Firebase:', error);
  throw error;
}

// Export initialized Firebase services
export { app, auth, db, storage, connectFirestoreEmulator, connectAuthEmulator, connectStorageEmulator };